import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SiLine } from 'react-icons/si';
import { FaWeixin } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onLineLogin: () => void;
  onEmailLogin?: () => void;
}

const isInWechat = () => /MicroMessenger/i.test(navigator.userAgent);

const LoginModal: React.FC<Props> = ({ isOpen, onClose, onLineLogin, onEmailLogin }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language?.startsWith('zh');
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.matchMedia('(min-width: 1024px)').matches);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-sm rounded-3xl bg-white shadow-elevated animate-in zoom-in-95 duration-200">

        <div className="flex items-center justify-between px-6 pt-6 pb-0">
          <h3 className="text-lg font-bold text-navy">
            {isZh ? '登录 / 注册' : 'ログイン / 新規登録'}
          </h3>
          <button onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-6 flex flex-col gap-4">
          {isZh ? (
            <>
              {/* WeChat QR */}
              <div className="flex justify-center">
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#07c160]/20 bg-[#07c160]/5 px-6 py-4">
                  <div className="w-36 h-36 rounded-xl overflow-hidden bg-white border border-slate-100">
                    <img src="/images/wechat_mp_qr.jpg" alt="微信公众号" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-bold text-[#07c160]">
                    <FaWeixin size={13} />
                    <span>微信公众号</span>
                  </div>
                </div>
              </div>

              {/* WeChat action — context-aware, no second modal */}
              {isInWechat() ? (
                <button
                  onClick={() => { window.location.href = '/api/wechat-mp/login'; }}
                  className="w-full flex items-center justify-center gap-3 rounded-2xl bg-[#07c160] px-6 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <FaWeixin size={22} />
                  微信一键登录
                </button>
              ) : isDesktop ? (
                <>
                  <p className="text-center text-sm text-slate-500 leading-relaxed">
                    电脑端用户可使用微信「扫一扫」直接关注，或点击下方按钮登录
                  </p>
                  <button
                    onClick={() => { window.location.href = '/api/wechat/login'; }}
                    className="w-full flex items-center justify-center gap-3 rounded-2xl bg-[#07c160] px-6 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <FaWeixin size={22} />
                    微信扫码登录
                  </button>
                </>
              ) : (
                <p className="text-center text-sm text-slate-500 leading-relaxed px-2">
                  长按上方二维码保存图片，打开微信「扫一扫」→识别图中二维码，即可关注公众号并登录。
                </p>
              )}
            </>
          ) : (
            <>
              {/* LINE QR */}
              <div className="flex justify-center">
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#06c755]/20 bg-[#06c755]/5 px-6 py-4">
                  <div className="w-36 h-36 rounded-xl overflow-hidden bg-white border border-slate-100">
                    <img src="/images/line_qr.png" alt="LINE公式アカウント" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-bold text-[#06c755]">
                    <SiLine size={12} />
                    <span>LINE公式アカウント</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => { onClose(); onLineLogin(); }}
                className="w-full flex items-center justify-center gap-3 rounded-2xl bg-[#06c755] px-6 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <SiLine size={22} />
                LINEで始める
              </button>
            </>
          )}

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400 font-medium">{isZh ? '或者' : 'または'}</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <button
            onClick={() => {
              onClose();
              if (onEmailLogin) {
                onEmailLogin();
              } else {
                navigate('/register');
              }
            }}
            className="w-full py-1 text-sm font-semibold text-slate-400 hover:text-coral transition-colors"
          >
            {isZh ? '邮箱登录注册 →' : 'メールアドレスで登録・ログイン →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
